/* eslint-disable max-lines */
// service
export interface IGetReservationV2 {
  pageIndex?: number;
  pageSize?: number;
  dateTo?: string;
  dateFrom?: string;
  searchKeyword?: string;
  filterByJourneyDate?: boolean;
  statusId?: number;
  /**
   * Comma-separated add-on facet (see components/reservation_v2/addOnFilters). Selections are
   * OR-ed server-side. Empty string means "no add-on filter" and is dropped by serialize().
   */
  addOns?: string;
}

export interface IGetReservationDetails {
  uniqueJourneyId: string;
}

export interface IEditReservationDetails {
  uniqueJourneyId: string;
  paymentStatus: number;
  email: string;
  paymentMethodId: number;
  tickets: {
    Id: number;
    tripId: number;
    journeyDate: string;
  }[];
  refundAmount?: number;
}

export interface IChangeJourneyTicket {
  uniqueJourneyId: string;
  body: {
    ticketId: number;
    tripId: number;
    journeyDate: string;
    ticketEmail: string;
    paymentMethodId: number;
  };
}

export interface IChangeJourneyStatus {
  uniqueJourneyId: string;
  ticketStatus: number;
  paymentMethodId: number;
}

// service end

export type IClientDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

export type ITicket = {
  ticketId: number;
  tripId: number;
  stationFrom: number;
  departureStationName: string;
  stationTo: number;
  arrivalStationName: string;
  journeyDate: string;
  email: string;
  countryId: number;
  operatorName: string;
};

export type ITicketInvoiceOnlyCancellation = {
  id: number;
  ticketId: number;
  journeyReservationId: number;
  uniqueJourneyId: string;
  operatorId: number;
  operatorName: string;
  canceledByUserId: number;
  reason: string | null;
  canceledAtUtc: string;
  consumed: boolean;
  consumedByInvoiceId: number | null;
};

// Mirrors backend Core.Models.Enums.OpenReturnEnums.OpenReturnStatus
export enum OpenReturnStatusEnum {
  Pending = 1,
  Selected = 2,
  Expired = 3,
  Canceled = 4
}

// Mirrors backend OpenReturnEntitlementDto
export interface IOpenReturnEntitlement {
  id: number;
  status: OpenReturnStatusEnum;
  statusText: string;
  extraPaidAmount: number;
  expiresAtUtc: string;
  canSelectReturn: boolean;
  returnFromStationId: number;
  returnToStationId: number;
  returnFromStationName: string | null;
  returnToStationName: string | null;
  ticketCount: number;
  selectedReturnDateTime: string | null;
  returnSelectedAtUtc: string | null;
}

// Mirrors backend SelectOpenReturnRequest / ReturnTripSelection.
// Stations are optional (backend int?): when omitted, the backend derives them
// from the trip line's endpoints (full-line return).
export interface IReturnTripSelection {
  tripId: number;
  journeyDateTime: string;
  departureStationId?: number | null;
  arrivalStationId?: number | null;
}

export interface ISelectOpenReturnRequest {
  trips: IReturnTripSelection[];
}

export interface IReservationV2 {
  journeyGuid: string;
  uniqueJourneyId: string;
  price: number;
  paymentStatus: number;
  paymentMethodId: number;
  oneWayTicketDetails: ITicket[];
  roundWayTicketDetails: ITicket[];
  clientNames: IClientDetails[];
  conciergeName: string;
  hasTextMeMyTrip?: boolean;
  hasOpenReturn?: boolean;
  openReturn?: IOpenReturnEntitlement | null;
  flexType: string;
  insertDate: string;
  invoiceOnlyCancellations: ITicketInvoiceOnlyCancellation[];
  cancellationSource?: string;
  // True when the reservation has a live (non-cancelled) Luggage Storage add-on.
  // Mirrors hasTextMeMyTrip / hasOpenReturn and drives the luggage tag.
  hasLuggage?: boolean;
}

export interface IReservationFormSchemaV2 {
  uniqueJourneyId: string;
  paymentStatus: { label: string; value: number };
  email: string;
  paymentMethod: { label: string; value: number };
  oneWayTicketDetails: ITicket[];
  roundWayTicketDetails: ITicket[];
  refundAmount?: number;
}

export interface IReservationForm {
  details: IReservationV2;
  onSelected?: () => void;
}

export interface ITripInfo {
  ticketDetailsFor: "oneWayTicketDetails" | "roundWayTicketDetails";
  showReturn: boolean;
  invoiceOnlyCancellations: ITicketInvoiceOnlyCancellation[];
}

export interface IGetLineId {
  lineId: number;
  reverse: boolean | undefined;
}

export interface IAvailableDates {
  nextRouteDateTime: string;
  routeCount: number;
  routeType: number;
}

// ---- Passengers (admin names modal) ----
// Mirrors backend Core.Models.Api.Journey.AdminPassengerDto / AdminPassengersDto, returned by
// GET Reservation/AdminPassengers/{uniqueJourneyId} and by the edit endpoint (SuperAdmin only).
export interface IAdminPassenger {
  /** TicketUserInfo.Id — the write endpoint addresses passengers by this, never by position. */
  id: number;
  firstName: string;
  lastName: string;
}

export interface IAdminPassengers {
  uniqueJourneyId: string;
  journeyPaymentStatus: number;
  /**
   * TicketUserInfo rows written by the last edit, across every leg. A name is stored once per
   * ticket, so a round trip with a transfer writes several rows per passenger. 0 on a read.
   */
  rowsUpdated: number;
  passengers: IAdminPassenger[];
}

// ---- Luggage Storage (admin details modal) ----
// Mirrors backend Core.Models.Api.Journey.AdminLuggageDetailsDto, returned by
// GET Reservation/AdminLuggageDetails/{uniqueJourneyId} (SuperAdmin only).
export type LuggagePartnerStatus = "confirmed" | "cancelled" | "test" | null;

export interface ILuggageDetails {
  uniqueJourneyId: string;
  bagCount: number;
  dropOffDate: string; // yyyy-MM-ddTHH:mm:ss (date-only meaning)
  pickUpDate: string;
  dayCount: number;
  cost: number;
  currency: string;
  partnerTirCode: string | null;
  partnerBookingId: string | null;
  partnerBookingStatus: LuggagePartnerStatus;
  journeyPaymentStatus: number;
  /** SuperAdmin edits already applied; editing is blocked at maxEditCount. */
  editCount: number;
  maxEditCount: number;
  /**
   * True when the add-on is no longer live (admin-cancelled, or the whole journey is
   * cancelled). The modal renders read-only: disabled actions + a "cancelled" title.
   */
  isCancelled: boolean;
}

// ---- Luggage Storage (admin edit/cancel) ----
// Mirrors backend Core.Models.Api.ReservationCalculation.ReservationCalculationModel,
// the body returned (as Response<T>.data) by
// POST Reservation/AdminEditLuggageStorage/{uniqueJourneyId}/{bagCount}. The edit is
// "display only" server-side (no refund/charge); `luggageFee`/`totalPrice` reflect the
// recalculated reservation after the change.
export interface IReservationCalculation {
  journeyId: string;
  totalPrice: number;
  price: number;
  pricePerTicket: number;
  serviceFee: number;
  reservationPromotionCodeMappingId: number | null;
  promotionCodeDiscount: number;
  flexiRideType: string;
  flexiRideFee: number;
  esimCost: number;
  esimPackageName: string;
  travelConciergeName: string;
  travelConciergeFee: number;
  textMeMyTripFee: number;
  ticketCount: number;
  openReturnFee: number;
  luggageFee: number;
  serviceFeePercentage: number | null;
  creditsDiscount: number;
}
